import {
  DisposalDialog,
  ValuationForm,
  VehicleForm,
  VehicleList,
  VehicleValueChart,
  useVehiclesPage,
} from "@gladis/domain-vehicles/ui";
import { useApiQuery } from "@gladis/api-client";
import type { CurrentUserDto, MemberDto, VehicleDto } from "@gladis/shared";
import { Button, ErrorState, LoadingState, Panel } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/vehicles")({
  component: Vehicles,
});

/**
 * Vehicles.
 *
 * Composition only — the chart, the list and both forms come from the vehicles package, which owns
 * what a vehicle is. This route decides where they appear, holds the dialog state, and decides where
 * a click on a vehicle goes.
 *
 * IT COMPOSES NOTHING FROM ANOTHER CONTEXT. A vehicle's cost figures arrive already resolved: the
 * API joins the linked expenses' monthly equivalents server-side, because that arithmetic belongs to
 * `monthlyEquivalent` in the expenses package and must have exactly one implementation.
 *
 * ==========================================================================================
 * TWO SECTIONS, EACH IN A PANEL WITH A NAMED SUBJECT
 * ==========================================================================================
 *
 * The table used to sit unframed and unheaded directly under the page title, which 019 records as a
 * real defect rather than a matter of taste: its net worth chart in that state "was reported as a
 * missing feature by somebody looking straight at it". `Panel` is `@gladis/ui`'s now — vehicles is
 * the fourth consumer, and that fourth copy is what the properties one asked to be promoted.
 *
 * THE CHART IS FIRST because it answers the question the page exists for — is what we own holding
 * its value — and the table answers "which ones, and what do they cost". Same order as every other
 * pillar page and as the Dashboard's own reading order.
 *
 * ==========================================================================================
 * A COST IS ADDED ON THE VEHICLE'S OWN PAGE, NOT HERE
 * ==========================================================================================
 *
 * This page carried a "Costs" dialog per row that listed every recurring expense in the household
 * with an Attach button beside each. It is gone. A member adds a cost underneath the car it belongs
 * to, on that car's page, and what gets written is an ordinary recurring expense plus a link — which
 * is the arrangement that makes the grouped row on the Expenses page mean anything.
 */
function Vehicles(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const { data, isPending, error } = useVehiclesPage();

  /*
   * The member list, for the owner picker — the same shape every other module page uses.
   *
   * Served from the cache `_authenticated` already populated for `/me`, and fetched only once there
   * is a household to ask about.
   */
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;
  const memberList = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );
  const members = memberList.data?.members ?? [];

  const [addOpen, setAddOpen] = useState(false);
  /*
   * WHICH VEHICLE IS BEING VALUED, held here rather than in the form.
   *
   * The form captures it again on open — see its own comment — because this is dropped the moment
   * the dialog closes, and the form must keep saying what it was about until it is gone.
   */
  const [valuing, setValuing] = useState<VehicleDto | null>(null);
  /*
   * SEPARATE STATE FROM `valuing`, because selling is a separate act from valuing — and from
   * archiving, which this page deliberately does not offer beside it. Income draws the same line
   * between ending and archiving, and collapsing them empties a past year.
   */
  const [selling, setSelling] = useState<VehicleDto | null>(null);

  return (
    <Page>
      <Page.Title
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            {t("veh.page.add")}
          </Button>
        }
      >
        {t("veh.page.title")}
      </Page.Title>

      <Page.Body>
        {/*
          `col-span-full`, because `Page.Body` IS A TWELVE-COLUMN GRID.

          A child with no span claims ONE column, so the table and the empty state both rendered at a
          twelfth of the page — narrow, legible, and wrong in a way that looks deliberate. Every other
          module page wraps its content this way; `expenses.tsx` and `income.tsx` use the identical
          line. The grid exists so a future summary card can sit at four columns beside something
          else, and until then everything on this page is full width.
        */}
        <div className="col-span-full flex flex-col gap-6">
          {isPending ? <LoadingState>{t("identity.loading")}</LoadingState> : null}
          {error ? <ErrorState>{error.message}</ErrorState> : null}

          {data ? (
            <>
              <Panel title={t("veh.chart.heading")} description={t("veh.chart.hint")}>
                <VehicleValueChart points={data.chart} />
              </Panel>

              <Panel title={t("veh.list.heading")} description={t("veh.list.hint")}>
                <VehicleList
                  vehicles={data.vehicles}
                  onOpen={(vehicle) => {
                    void navigate({
                      to: "/vehicles/$vehicleId",
                      params: { vehicleId: vehicle.id },
                    });
                  }}
                  onValue={setValuing}
                  onDispose={setSelling}
                />
              </Panel>
            </>
          ) : null}
        </div>
      </Page.Body>

      <VehicleForm
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        members={members}
      />

      <ValuationForm
        open={valuing !== null}
        onClose={() => {
          setValuing(null);
        }}
        vehicleId={valuing?.id ?? ""}
        vehicleName={valuing?.name ?? ""}
        firstRegisteredOn={valuing?.firstRegisteredOn ?? ""}
      />

      <DisposalDialog
        open={selling !== null}
        onClose={() => {
          setSelling(null);
        }}
        vehicleId={selling?.id ?? ""}
        vehicleName={selling?.name ?? ""}
        lastValuationInEuros={selling?.currentValueInEuros ?? null}
      />
    </Page>
  );
}
